import React, { useState, useRef, useEffect } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Forward10Icon from "@mui/icons-material/Forward10";
import Replay10Icon from "@mui/icons-material/Replay10";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {
  Box,
  IconButton,
  Slider,
  Menu,
  MenuItem,
  Typography,
  Avatar
} from "@mui/material";

const AudioPlayer = ({
  src,
  styles,
  setIsAudioFinished,
  isSliderNeeded = true,
  coverImage = null
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const [volumeMenuAnchor, setVolumeMenuAnchor] = useState(null);
  const [volume, setVolume] = useState(1);
  const audioRef = useRef(null);

  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  // Using the original theme colors
  const primaryColor = "#368AD0";
  const lightGrayColor = styles?.lightGreyColor || "#94A3B8";

  // Format time in MM:SS format
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || timeInSeconds === Infinity) return "00:00";

    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Toggle play/pause
  const togglePlay = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play().catch((err) => {
          console.error("Error playing audio:", err);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Handle volume change
  const handleVolumeChange = (event, newValue) => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newValue;
      setVolume(newValue);
      if (newValue === 0) {
        setIsMuted(true);
        audio.muted = true;
      } else if (isMuted) {
        setIsMuted(false);
        audio.muted = false;
      }
    }
  };

  // Handle volume menu
  const handleVolumeMenuOpen = (event) => {
    setVolumeMenuAnchor(event.currentTarget);
  };

  const handleVolumeMenuClose = () => {
    setVolumeMenuAnchor(null);
  };

  // Seek forward 10 seconds
  const seekForward = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.min(audio.currentTime + 10, duration || 0);
      setCurrentTime(audio.currentTime);
    }
  };

  // Seek backward 10 seconds
  const seekBackward = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(audio.currentTime - 10, 0);
      setCurrentTime(audio.currentTime);
    }
  };

  // Handle playback speed change
  const handleSpeedChange = (speed) => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = speed;
      setPlaybackRate(speed);
      setSpeedMenuAnchor(null);
    }
  };

  // Handle speed menu
  const handleSpeedMenuOpen = (event) => {
    setSpeedMenuAnchor(event.currentTarget);
  };

  const handleSpeedMenuClose = () => {
    setSpeedMenuAnchor(null);
  };

  // Update duration and handle events when audio is loaded
  useEffect(() => {
    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      if (
        audio.duration &&
        !isNaN(audio.duration) &&
        audio.duration !== Infinity
      ) {
        setDuration(audio.duration);
        setIsLoading(false);
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setCurrentTime(0);
      setIsPlaying(false);
      if (setIsAudioFinished) setIsAudioFinished(true);
    };

    const handleError = (error) => {
      console.error("Audio error:", error);
      setIsLoading(false);
    };

    if (audio) {
      setIsLoading(true);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("canplay", handleCanPlay);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);

      // Set playback rate in case audio was already loaded
      audio.playbackRate = playbackRate;
      audio.volume = volume;
      audio.muted = isMuted;
    }

    return () => {
      if (audio) {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("canplay", handleCanPlay);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("error", handleError);
      }
    };
  }, [src, setIsAudioFinished]);

  // Handle progress bar change
  const handleSliderChange = (event, newValue) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = newValue;
      setCurrentTime(audio.currentTime);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        background: styles.inputLabelColor,
        borderRadius: "10px",
        padding: "12px"
      }}
    >
      {/* Playback Controls and Progress */}
      <Box display="flex" alignItems="center" width="100%">
        {/* Control buttons */}
        <IconButton
          sx={{
            p: 0.5,
            color: isLoading ? "#CCCCCC" : lightGrayColor,
            "&:hover": { color: primaryColor }
          }}
          onClick={seekBackward}
          disabled={isLoading || !duration}
          size="small"
        >
          <Replay10Icon fontSize="small" />
        </IconButton>

        <IconButton
          sx={{
            padding: "6px",
            borderRadius: "50%",
            background: isLoading ? "#E2E8F0" : primaryColor,
            color: "#FFFFFF",
            mx: 0.5,
            "&:hover": {
              background: isLoading ? "#E2E8F0" : primaryColor,
              opacity: 0.9
            },
            "&:disabled": {
              backgroundColor: "#E2E8F0",
              color: "#94A3B8"
            }
          }}
          onClick={togglePlay}
          disabled={isLoading || !duration}
          size="small"
        >
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: 18 }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>

        <IconButton
          sx={{
            p: 0.5,
            color: isLoading ? "#CCCCCC" : lightGrayColor,
            "&:hover": { color: primaryColor }
          }}
          onClick={seekForward}
          disabled={isLoading || !duration}
          size="small"
        >
          <Forward10Icon fontSize="small" />
        </IconButton>

        {/* Time and Progress */}
        {isSliderNeeded && (
          <>
            <Typography
              variant="caption"
              sx={{
                color: lightGrayColor,
                mx: 1,
                fontSize: "0.7rem",
                minWidth: "32px"
              }}
            >
              {formatTime(currentTime)}
            </Typography>

            <Slider
              value={currentTime}
              max={duration || 1}
              onChange={handleSliderChange}
              disabled={isLoading || !duration}
              size="small"
              sx={{
                color: primaryColor,
                mx: 0.5,
                flex: 1,
                height: 3,
                padding: "4px 0",
                "& .MuiSlider-thumb": {
                  width: 8,
                  height: 8,
                  "&:hover, &.Mui-focusVisible": {
                    boxShadow: `0 0 0 6px ${primaryColor}30`
                  }
                },
                "& .MuiSlider-track": {
                  backgroundColor: primaryColor,
                  border: "none"
                },
                "& .MuiSlider-rail": {
                  backgroundColor: "#E1F5E6"
                }
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: lightGrayColor,
                fontSize: "0.7rem",
                minWidth: "32px",
                textAlign: "right"
              }}
            >
              {isLoading ? "--:--" : formatTime(duration)}
            </Typography>
          </>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "2px 8px",
            borderRadius: "12px",
            bgcolor: `${primaryColor}`,
            ml: 2,
            mr: 1,
            cursor: "pointer"
          }}
          onClick={handleSpeedMenuOpen}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: styles.color,
              fontSize: "0.75rem"
            }}
          >
            {playbackRate}x
          </Typography>
        </Box>

        <Menu
          anchorEl={speedMenuAnchor}
          open={Boolean(speedMenuAnchor)}
          onClose={handleSpeedMenuClose}
        >
          {speedOptions.map((speed) => (
            <MenuItem
              key={speed}
              onClick={() => handleSpeedChange(speed)}
              selected={playbackRate === speed}
              sx={{
                fontWeight: playbackRate === speed ? 600 : 400,
                color: playbackRate === speed ? primaryColor : "inherit",
                py: 0.5,
                minWidth: "80px"
              }}
            >
              {speed}x
            </MenuItem>
          ))}
        </Menu>

        {/* Volume Control */}
        <IconButton
          sx={{
            p: 0.5,
            color: isLoading ? "#CCCCCC" : lightGrayColor,
            "&:hover": { color: primaryColor }
          }}
          onClick={handleVolumeMenuOpen}
          disabled={isLoading || !duration}
          size="small"
        >
          {isMuted ? (
            <VolumeOffIcon fontSize="small" />
          ) : (
            <VolumeUpIcon fontSize="small" />
          )}
        </IconButton>

        <Menu
          anchorEl={volumeMenuAnchor}
          open={Boolean(volumeMenuAnchor)}
          onClose={handleVolumeMenuClose}
          sx={{ p: "0 !important" }}
          style={{padding: "10px !important"}}
        >
          <Box
            sx={{
              width: 160,
              display: "flex",
              alignItems: "center",
              px: 1
              // py: 1
            }}
          >
            <VolumeOffIcon
              fontSize="small"
              sx={{ color: lightGrayColor, mr: 1 }}
            />
            <Slider
              value={isMuted ? 0 : volume}
              min={0}
              max={1}
              step={0.01}
              onChange={handleVolumeChange}
              sx={{
                flexGrow: 1,
                color: primaryColor,
                "& .MuiSlider-thumb": {
                  width: 10,
                  height: 10
                },
                "& .MuiSlider-track": {
                  backgroundColor: primaryColor
                },
                "& .MuiSlider-rail": {
                  backgroundColor: "#E1F5E6"
                }
              }}
            />
            <VolumeUpIcon
              fontSize="small"
              sx={{ color: lightGrayColor, ml: 1 }}
            />
          </Box>
        </Menu>
      </Box>

      <audio ref={audioRef} src={src} preload="metadata" />
    </Box>
  );
};

export default AudioPlayer;
